import { Dvh } from '~/domain/patient/dvh';

/** One plotted point of a cumulative DVH: dose in Gy against % of volume. */
export type DvhPoint = { x: number; y: number };

/**
 * Turn a cumulative DVH histogram into the points the chart plots.
 *
 * `Dvh.dvhData[i]` is the percentage of the structure's volume receiving at
 * least `i * binWidth` Gy, so a point's x is its bin's LEFT edge.
 *
 * Two things this must not do, both of which are dose readouts the user can
 * act on:
 *
 * 1. **Never plot past the dose the structure receives.** The histogram is
 *    allocated from the *global* dose maximum (rounded up) and is the same
 *    length for every structure, so its upper bins are empty for all but
 *    the hottest one — a 45 Gy bladder against a 77.3 Gy plan carried ~660
 *    zero-volume points out to 78.05 Gy. They are invisible (y = 0) but
 *    they are real data points, and the chart's hover snaps to the nearest
 *    one, so pointing anywhere along the bottom axis read back a dose far
 *    above the structure's maximum — and above the plan's.
 *
 *    Points therefore stop one bin past the last bin carrying volume, and
 *    every x is clamped to the structure's own reported maximum. Trimming
 *    alone is not enough: the closing bin sits a full bin width above the
 *    last dose, and `addIntervalVolume` smears a uniform-dose interval by
 *    half a bin either side, so even the last volume-carrying bin can start
 *    above the reported max. Both are well under a bin (0.05 Gy), but the
 *    tooltip renders the hovered dose and the "Max" label as two separately
 *    `toFixed(1)`-rounded numbers, which shows that sub-bin gap as a full
 *    0.1 Gy — a structure whose max reads 45.4 Gy inviting a 45.5 Gy
 *    reading off its own curve.
 * 2. **Never interpolate.** Every point is a bin the histogram actually
 *    holds; the closing point is the histogram's own next bin, which is
 *    zero by construction. The chart reads these values back verbatim.
 *
 * @param dvh Cumulative histogram for one structure. Its `doseMaxGy` is the
 *   unscaled maximum dose attributed to the structure.
 * @param gridMaxGy The dose grid's global maximum in Gy, already multiplied
 *   by `doseScale`. A second ceiling below the per-structure one, so a
 *   structure max that ever exceeded the plan's could not reach the chart.
 * @param doseScale Dimensionless display multiplier applied to the dose axis.
 */
export function buildDvhPoints(
    dvh: Dvh,
    gridMaxGy: number,
    doseScale: number
): DvhPoint[] {
    const lastWithVolume = lastIndexWithVolume(dvh.dvhData);
    if (lastWithVolume < 0) {
        // No volume at any dose — an empty structure, or one that fell
        // entirely outside the dose grid. Draw nothing rather than a flat
        // line along the axis that invites a hover reading.
        return [];
    }

    const binGy = dvh.binWidth * doseScale;
    // The structure's own maximum is the ceiling, not just the grid's: the
    // tooltip shows both this and the hovered point, so any plotted dose
    // above it reads as the curve contradicting its own headline number.
    const ceilingGy = Math.min(dvh.doseMaxGy * doseScale, gridMaxGy);
    // One past the last bin with volume, so the curve drops to 0 % instead
    // of ending mid-air. The clamp keeps that closing point at the maximum.
    const lastPlotted = lastWithVolume + 1;

    const points: DvhPoint[] = [];
    for (let i = 0; i <= lastPlotted; i++) {
        const x = i * binGy;
        points.push({
            x: x > ceilingGy ? ceilingGy : x,
            y: i < dvh.dvhData.length ? dvh.dvhData[i] : 0,
        });
    }
    return points;
}

function lastIndexWithVolume(data: Float64Array): number {
    for (let i = data.length - 1; i >= 0; i--) {
        if (data[i] > 0) {
            return i;
        }
    }
    return -1;
}
